import React, { ChangeEvent, useState } from 'react';

import { useSnackbar } from 'notistack';

import { FileDownload } from '@mui/icons-material';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useAutoSetupProperties } from '~/hooks/apis/vendors/useAutoSetupProperties';

import PropertiesUploadResult from './PropertiesUploadResult';

const TEMPLATE_HEADER =
  'Vendor Id,Name,Address,Latitude,Longitude,City,State/Country,Max Child Age,Max Infant Age,Channel Manager,Hotel Code,Channel Supplier Id,Offer Id';

interface Props {
  isModalOpen: boolean;
  closeModal: () => void;
}

export default function PropertiesCsvUploadModal({ isModalOpen, closeModal }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [isAutoPopulating, setIsAutoPopulating] = useState(false);
  const autoSetupMutation = useAutoSetupProperties();

  const handleClose: DialogProps['onClose'] = (_event, reason) => {
    if (autoSetupMutation.isPending || isAutoPopulating) {
      return;
    }
    if (reason === 'backdropClick' && (autoSetupMutation.isSuccess || autoSetupMutation.isError)) {
      return;
    }
    closeModal();
  };

  const handleDownloadTemplate = () => {
    const blob = new Blob([TEMPLATE_HEADER], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk-properties-upload-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] ?? null);
  };

  const handleSubmit = () => {
    if (!file) {
      enqueueSnackbar('Please select a CSV file', { variant: 'warning' });
      return;
    }
    autoSetupMutation.mutate(file, {
      onSuccess: () => {
        enqueueSnackbar('Properties uploaded successfully', { variant: 'success' });
      },
      onError: (error: Error) => {
        enqueueSnackbar(`Upload failed: ${error.message}`, { variant: 'error' });
      },
    });
  };

  return (
    <Dialog
      open={isModalOpen}
      onClose={handleClose}
      onTransitionExited={() => {
        setFile(null);
        autoSetupMutation.reset();
      }}
      disableEscapeKeyDown={autoSetupMutation.isPending || isAutoPopulating}
      maxWidth={autoSetupMutation.isSuccess ? 'md' : 'sm'}
      fullWidth
    >
      <DialogTitle>New LPC Properties Upload</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {autoSetupMutation.isPending && (
            <Alert severity="info" icon={<CircularProgress size={20} />}>
              Processing upload, please do not close this page...
            </Alert>
          )}

          {autoSetupMutation.isSuccess && (
            <PropertiesUploadResult
              result={autoSetupMutation.data.result}
              onAutoPopulatingChange={setIsAutoPopulating}
            />
          )}
          {autoSetupMutation.isError && (
            <Alert severity="error">Upload failed: {autoSetupMutation.error.message}</Alert>
          )}

          {!autoSetupMutation.isSuccess && !autoSetupMutation.isPending && (
            <>
              <Alert severity="info">
                This is for DerbySoft LPC properties only. Other channel managers/offer types are not supported by this
                upload. Max 500 properties per upload.
              </Alert>
              <Stack spacing={1}>
                <Typography variant="body2">
                  Need the template? Download it first, fill in your data, then upload:
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={handleDownloadTemplate}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Download template (.csv)
                </Button>
              </Stack>
              <TextField
                type="file"
                onChange={handleFileChange}
                disabled={autoSetupMutation.isPending}
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: '.csv' }}
                fullWidth
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          onClick={() => handleClose(undefined, undefined)}
          disabled={autoSetupMutation.isPending || isAutoPopulating}
        >
          {autoSetupMutation.isSuccess || autoSetupMutation.isError ? 'Close' : 'Cancel'}
        </Button>
        {!autoSetupMutation.isSuccess && (
          <Button variant="contained" onClick={handleSubmit} disabled={!file || autoSetupMutation.isPending}>
            {autoSetupMutation.isPending && <CircularProgress size={20} sx={{ mr: 1 }} />}
            {autoSetupMutation.isPending ? 'Uploading...' : 'Upload'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
